import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ClassMembership } from '../class-membership.js';
import { ClassRoom } from '../classroom.js';
import { Exam } from '../exam.js';
import { Teacher } from '../teacher.js';

/**
 * Deletes one of the current teacher's classes.
 *
 * WHAT IS AND IS NOT DESTROYED, because a class is referenced from two directions and the two deserve
 * opposite treatment:
 *  - `ClassMembership` rows are deleted. A membership is a row ABOUT this class — approved students and
 *    pending join requests alike have nothing left to point at once it is gone, and leaving them would
 *    strand rows that every roster query joins on.
 *  - `Exam`s are NEVER deleted, only unlinked (`classRoomID: null`). An exam is the teacher's work, held
 *    by `userID`, and merely tagged with a class; destroying graded work as a side effect of tidying up a
 *    class list would be indefensible. `Exam.class` (the display string) is deliberately left alone too,
 *    so the exam still says which class it was written for.
 *
 * `Teacher.classes` is also left alone: it accumulates class NAMES from exams (see
 * `syncTeacherClassFromExam`) and those exams still carry the name, so the entry is still true.
 *
 * The bulk writes go through `getRawCollection`: the wrapped collection from `getCollection` rejects
 * `deleteMany`/`updateMany` outright (see `wrapCollection` in baseql's collections.ts). That bypasses the
 * row-level publish filter, so both writes are scoped by `classRoomID` — an id this resolver has already
 * proven belongs to the calling teacher.
 */
export const deleteClassroomMutation = registerMutation({
  operationName: 'deleteClassroomMutation',
  type: () => Boolean,
  params: [{ name: 'classRoomID', type: () => String }],

  resolve: (classRoomID: string) => async (): Promise<boolean> => {
    const trimmedClassRoomID = typeof classRoomID === 'string' ? classRoomID.trim() : '';
    if (!trimmedClassRoomID) throw new Error('Klasse nicht gefunden');

    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    if (!currentUser?._id) throw new Error('Nicht autorisiert: Kein User eingeloggt');

    const teacherCollection = await getCollection(Teacher);
    const teacher = await teacherCollection.findOne({ userID: currentUser._id });
    if (!teacher) throw new Error('Nicht autorisiert: Kein Lehrerprofil gefunden');

    const classroomCollection = await getCollection(ClassRoom);
    const classroom = await classroomCollection.findOne({ _id: trimmedClassRoomID });
    if (!classroom) throw new Error('Klasse nicht gefunden');
    if (classroom.teacherID !== teacher._id) throw new Error('Nicht autorisiert: Diese Klasse gehört dir nicht');

    // Exams first, memberships second, the class itself last: every step before the last leaves the class
    // in a consistent (if emptier) state, so a failure part-way through never leaves rows pointing at a
    // class that no longer exists. Re-running the mutation then simply finishes the job.
    const rawExams = await getRawCollection(Exam._collectionName);
    const unlinkResult = await rawExams.updateMany(
      { classRoomID: trimmedClassRoomID },
      { $set: { classRoomID: null } },
    );

    const rawMemberships = await getRawCollection(ClassMembership._collectionName);
    const membershipResult = await rawMemberships.deleteMany({ classRoomID: trimmedClassRoomID });

    await classroomCollection.deleteOne({ _id: trimmedClassRoomID });

    logger.info(
      {
        classRoomID: trimmedClassRoomID,
        teacherID: teacher._id,
        removedMemberships: membershipResult?.deletedCount ?? 0,
        unlinkedExams: unlinkResult?.modifiedCount ?? 0,
      },
      'Classroom deleted',
    );

    return true;
  },
});
